import type {
	AdminConfig,
	AuthTokenResponse,
	ContentQueryResult,
	ContentSourceData,
	DigitalEditionData,
	DigitalEditionListing,
	DigitalEditionPositionsData,
	EditionSummary,
	LayoutData,
	PrintEditionData,
	PrintEditionListing,
	ProductData,
	SummaryArticle
} from '../../types/types.ts';
import type {NordjyskeArticleData} from '../adapters/nordjyske-content-adapter.ts';
import {api} from './api-client.ts';
import type {ApiErrorResponse, ApiOKResponse} from './http.ts';
import {handleRpcResponse} from './http.ts';

export async function validateToken(token: string) {
	// Note: validateToken uses a custom auth header, so it cannot use the RPC client
	// which automatically injects the stored token. Keep using fetch directly.
	const response = await fetch('/api/auth/token', {
		headers: {
			accept: 'application/json',
			Authorization: `Bearer ${token}`
		},
		method: 'GET',
		mode: 'cors'
	});

	const body = await response.json();

	if (response.status === 200) {
		return {
			status: 200 as const,
			body: body as AuthTokenResponse
		};
	}

	return {
		status: response.status as 400 | 401 | 404,
		body: body
	};
}

export async function loadPrintProducts(): Promise<ApiOKResponse<ProductData[]> | ApiErrorResponse> {
	return handleRpcResponse<ProductData[]>(api['print-products'].$get());
}

export async function loadMagazines(): Promise<ApiOKResponse<ProductData[]> | ApiErrorResponse> {
	return handleRpcResponse<ProductData[]>(api.magazines.$get());
}

export async function loadFrontPages(): Promise<ApiOKResponse<ProductData[]> | ApiErrorResponse> {
	return handleRpcResponse<ProductData[]>(api['front-pages'].$get());
}

export async function loadProduct(productName: string): Promise<ApiOKResponse<ProductData> | ApiErrorResponse> {
	return handleRpcResponse<ProductData>(api.products[':name'].$get({param: {name: productName}}));
}

export async function loadRecentEditions(): Promise<ApiOKResponse<PrintEditionListing[]> | ApiErrorResponse> {
	return handleRpcResponse<PrintEditionListing[]>(api.editions.$get());
}

export async function loadRecentEditionsForProduct(
	productName: string,
	limit = 0
): Promise<ApiOKResponse<PrintEditionListing[]> | ApiErrorResponse> {
	return handleRpcResponse<PrintEditionListing[]>(
		api.products[':name'].editions.$get({
			param: {name: productName},
			query: limit > 0 ? {limit: limit.toString()} : {}
		})
	);
}

export async function loadRecentDigitalEditionsForProduct(
	productName: string,
	limit = 0
): Promise<ApiOKResponse<DigitalEditionListing[]> | ApiErrorResponse> {
	return handleRpcResponse<DigitalEditionListing[]>(
		api.products[':name']['digital-editions'].$get({
			param: {name: productName},
			query: limit > 0 ? {limit: limit.toString()} : {}
		})
	);
}

export async function loadPrintEdition(editionId: string): Promise<ApiOKResponse<PrintEditionData> | ApiErrorResponse> {
	return handleRpcResponse<PrintEditionData>(api.editions[':id'].$get({param: {id: editionId}}));
}

export async function loadDigitalEdition(
	editionId: string
): Promise<ApiOKResponse<DigitalEditionData> | ApiErrorResponse> {
	return handleRpcResponse<DigitalEditionData>(api['digital-editions'][':id'].$get({param: {id: editionId}}));
}

export async function loadDigitalEditionPositions(
	editionId: string
): Promise<ApiOKResponse<DigitalEditionPositionsData> | ApiErrorResponse> {
	return handleRpcResponse<DigitalEditionPositionsData>(
		api['digital-editions'][':id'].positions.$get({param: {id: editionId}})
	);
}

export async function loadContentSources(): Promise<ApiOKResponse<ContentSourceData[]> | ApiErrorResponse> {
	return handleRpcResponse<ContentSourceData[]>(api['content-sources'].$get());
}

export async function loadContentSource(
	sourceId: string
): Promise<ApiOKResponse<ContentSourceData> | ApiErrorResponse> {
	return handleRpcResponse<ContentSourceData>(api['content-sources'][':sourceId'].$get({param: {sourceId}}));
}

export async function executeQuery(
	sourceId: string,
	queryId: string,
	params = {}
): Promise<ApiOKResponse<ContentQueryResult> | ApiErrorResponse> {
	const queryParams = Object.entries(params).reduce(
		(acc, [key, value]) => {
			acc[key] = String(value);
			return acc;
		},
		{} as Record<string, string>
	);

	return handleRpcResponse<ContentQueryResult>(
		api['content-sources'][':sourceId'].execute[':queryId'].$get({
			param: {sourceId, queryId},
			query: queryParams
		})
	);
}

export async function executeSingleItemSource<T>(
	sourceId: string,
	itemId: string
): Promise<ApiOKResponse<T> | ApiErrorResponse> {
	return handleRpcResponse<T>(
		api['single-item-sources'][':sourceId'].execute.$get({
			param: {sourceId},
			query: {'params[itemId]': itemId}
		})
	);
}

export async function loadNordjyskeArticles(
	startDate: string,
	endDate: string,
	params = {}
): Promise<ApiOKResponse<NordjyskeArticleData[]> | ApiErrorResponse> {
	const queryParams = Object.entries(params).reduce(
		(acc, [key, value]) => {
			acc[key] = String(value);
			return acc;
		},
		{} as Record<string, string>
	);

	return handleRpcResponse<NordjyskeArticleData[]>(
		api.nordjyske.articles.$get({
			query: {
				startDate,
				endDate,
				...queryParams
			}
		})
	);
}

export async function loadNordjyskeArticle(
	articleId: string
): Promise<ApiOKResponse<NordjyskeArticleData> | ApiErrorResponse> {
	return handleRpcResponse<NordjyskeArticleData>(
		api.nordjyske.article.$get({
			query: {id: articleId}
		})
	);
}

export async function loadEditionLayouts(
	editionId: string
): Promise<ApiOKResponse<Record<string, string>> | ApiErrorResponse> {
	return handleRpcResponse<Record<string, string>>(
		api.editions[':id'].layouts.$get({
			param: {id: editionId}
		})
	);
}

export async function rewriteArticle(
	id: string,
	input: object
): Promise<
	| ApiOKResponse<{
			body: string;
			title: string;
			intro: string;
			story: string;
	  }>
	| ApiErrorResponse
> {
	return handleRpcResponse<{body: string; title: string; intro: string; story: string}>(
		api.ai.rewrite.$post({
			json: {id, input}
		})
	);
}

export async function loadLayoutData(
	uuid: string,
	version: number
): Promise<ApiOKResponse<LayoutData> | ApiErrorResponse> {
	return handleRpcResponse<LayoutData>(
		api['layout-data'][':uuid'][':version'].$get({
			param: {uuid, version: version.toString()}
		})
	);
}

export async function loadLayout(uuid: string, version: number): Promise<string | null> {
	const response = await api.layout[':uuid'][':version'].$get({
		param: {uuid, version: version.toString()}
	});
	if (!response.ok) return null;
	return response.text();
}

export async function loadEditionSummary(editionId: string): Promise<EditionSummary> {
	try {
		const response = await api.editions[':id'].summary.$get({param: {id: editionId}});
		if (response.ok) {
			return response.json() as Promise<EditionSummary>;
		}
		return {content: null, generatedAt: null, approved: false, error: 'failed_to_load'};
	} catch {
		return {content: null, generatedAt: null, approved: false, error: 'network_error'};
	}
}

export async function generateSummary(editionId: string, articles: SummaryArticle[]): Promise<EditionSummary> {
	try {
		const response = await api.summary.generate.$post({
			json: {editionId, articles}
		});
		if (response.ok) {
			return response.json() as Promise<EditionSummary>;
		}
		return {content: null, generatedAt: null, approved: false, error: 'failed_to_generate'};
	} catch {
		return {content: null, generatedAt: null, approved: false, error: 'network_error'};
	}
}

export async function loadAdminConfig(): Promise<AdminConfig | null> {
	try {
		const response = await api.admin.config.$get();
		if (response.ok) {
			return response.json() as Promise<AdminConfig>;
		}
		return null;
	} catch (error) {
		console.error('Failed to load admin config:', error);
		return null;
	}
}

export async function saveAdminConfig(config: Partial<AdminConfig>): Promise<boolean> {
	try {
		const response = await api.admin.config.$post({json: config});
		return response.ok;
	} catch (error) {
		console.error('Failed to save admin config:', error);
		return false;
	}
}
